/**
 * Firestore CRUD abstraction
 * All data is scoped to the current user
 */

import {
  collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  query, where, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { firestore } from './firebase.js';
import { auth } from './auth.js';

class FirestoreDB {
  /**
   * Get user-scoped collection reference
   */
  getUserCollection(collectionName) {
    const uid = auth.getUserId();
    if (!uid) throw new Error('User not authenticated');
    return collection(firestore, 'users', uid, collectionName);
  }

  /**
   * Read all documents from a collection
   */
  async read(collectionName) {
    const snapshot = await getDocs(this.getUserCollection(collectionName));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Read a single document by ID
   */
  async readOne(collectionName, docId) {
    const d = await getDoc(doc(this.getUserCollection(collectionName), docId));
    if (!d.exists()) return null;
    return { id: d.id, ...d.data() };
  }

  /**
   * Query documents with filters
   */
  async query(collectionName, filters = []) {
    const constraints = filters.map(([field, op, value]) => where(field, op, value));
    const q = query(this.getUserCollection(collectionName), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /**
   * Create a new document
   */
  async create(collectionName, data, docId = null) {
    const ts = serverTimestamp();
    const docData = { ...data, created_at: ts, updated_at: ts };

    if (docId) {
      await setDoc(doc(this.getUserCollection(collectionName), docId), docData);
      return docId;
    }
    const ref = doc(this.getUserCollection(collectionName));
    await setDoc(ref, docData);
    return ref.id;
  }

  /**
   * Update a document
   */
  async update(collectionName, docId, updates) {
    await updateDoc(doc(this.getUserCollection(collectionName), docId), {
      ...updates,
      updated_at: serverTimestamp(),
    });
  }

  /**
   * Delete a document
   */
  async delete(collectionName, docId) {
    await deleteDoc(doc(this.getUserCollection(collectionName), docId));
  }

  /**
   * Delete documents matching filters
   */
  async deleteWhere(collectionName, filters) {
    const docs = await this.query(collectionName, filters);
    const batch = writeBatch(firestore);
    docs.forEach((d) => {
      batch.delete(doc(this.getUserCollection(collectionName), d.id));
    });
    await batch.commit();
  }

  /**
   * Batch write operations
   */
  async batchWrite(operations) {
    const batch = writeBatch(firestore);
    const ts = serverTimestamp();

    operations.forEach((op) => {
      const ref = doc(this.getUserCollection(op.collection), op.id || this.generateId());

      switch (op.type) {
        case 'set':
          batch.set(ref, { ...op.data, updated_at: ts }, { merge: true });
          break;
        case 'update':
          batch.update(ref, { ...op.data, updated_at: ts });
          break;
        case 'delete':
          batch.delete(ref);
          break;
      }
    });

    await batch.commit();
  }

  /**
   * Generate a unique ID
   */
  generateId(prefix = 'doc') {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const db = new FirestoreDB();
